import { useHistory } from 'react-router-dom';
import { DeleteOutlineRounded, Whatshot, CheckCircleOutline, CachedRounded, FlagRounded } from '@mui/icons-material';
import { Typography, Button, Grid, CircularProgress } from '@mui/material';
import { red, blue, green, amber } from '@mui/material/colors';
import { DataGridPremium, useKeepGroupedColumnsHidden } from '@mui/x-data-grid-premium';
import TaskPriorityButton from '../../Components/TaskPriorityButton';
import { User, TooltipTypography, DateFormatter, UserGroup } from '../../../../Global/Components';
import { StatusButton } from '../../Components';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useDataGridPagination } from '../../../../Global/hooks';
import { isArray, isEmpty, isEqual } from 'lodash';
import { FormatDate } from '../../../../Global/Functions';
import axios from 'axios';
import StateManager from '../../../../Global/StateManager';
import TaskManager from '../../../../Global/TaskManager';
import {
  convertToTitleCase,
  disabledTypes,
  dueDateFilters,
  priorities,
  priorityColors,
  completedDateFilters,
} from '../../constants';
import TaskTypes from '../../../../Global/TaskTypes';
import moment from 'moment';
import UserInputValue from '../../../../Global/DataGrid/Filters/UserInput';
import TasksFilters from '../TasksFilters';
import OptionsInput from '../../../../Global/DataGrid/Filters/OptionsInput';
import StatusInput from '../../Components/StatusInput';

const PrioritiesOptions = priorities.map((priority) => ({
  _id: priority,
  title: priority,
  icon: <Whatshot style={{ color: priorityColors[priority] }} />,
}));

function formatDeltaForTable(value) {
  const diff = moment.duration(value);
  const minutes = Math.floor(Math.abs(diff.asMinutes()));
  const hours = Math.floor(Math.abs(diff.asHours()));
  const days = Math.floor(Math.abs(diff.asDays()));

  const color = value < 0 ? red[600] : green[600];

  let text = '';

  if (days === 1) {
    text = `${days} day`;
  }

  if (days >= 2) {
    text = `${days} days`;
  }

  if (days === 0) {
    if (hours > 0) {
      if (hours === 1) {
        text = `${hours} hour`;
      } else {
        text = `${hours} hours`;
      }
    } else {
      if (minutes === 1) {
        text = `${minutes} minute`;
      } else {
        text = `${minutes} minutes`;
      }
    }
  }

  return <span style={{ color, fontWeight: 500 }}>{text}</span>;
}

export default function TableView({
  pageType,
  teamId,
  portalId,
  searchTerm,
  dateView,
  taskInfo,
  includeRefColumns,
  fromPortal,
  forHomePage,
}) {
  const [selected, setSelected] = useState([]);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [viewCompleted, setViewCompleted] = useState(false);
  const viewCompletedRef = useRef();

  const history = useHistory();

  viewCompletedRef.current = viewCompleted;

  const onFiltersUpdate = (filterModel) => {
    const filterItems = [...filterModel.items];

    const dueFilterIndex = filterItems.findIndex((item) => item.field === 'dueAt');
    if (dueFilterIndex > -1) {
      const dueFilterModel = filterItems[dueFilterIndex];
      const newFilters = dueDateFilters[dueFilterModel?.operator];

      filterItems[dueFilterIndex] = {
        ...dueFilterModel,
        operator: newFilters?.operator,
        value: newFilters?.value,
      };
    }

    const completedFilterIndex = filterItems.findIndex((item) => item.field === 'completedDate');
    if (completedFilterIndex > -1) {
      const completedilterModel = filterItems[completedFilterIndex];
      const newFilters = dueDateFilters[completedilterModel?.operator];

      filterItems[completedFilterIndex] = {
        ...completedilterModel,
        operator: newFilters?.operator,
        value: newFilters?.value,
      };
    }

    gridConfig.gridProps.onFilterModelChange({ items: filterItems });
  };

  const columns = useMemo(() => {
    const dueAtColumn = {
      field: 'dueAt',
      headerName: 'Due',
      sortable: true,
      width: 150,
      alignItems: 'center',
      display: 'flex',
      groupable: false,
      filterOperators: Object.keys(dueDateFilters).map((filter) => ({
        label: convertToTitleCase(filter),
        value: filter,
      })),
      renderCell: ({ row, rowNode, formattedValue, field }) => {
        if (rowNode.type === 'group' && field === rowNode.groupingField) {
          return '';
        }
        const overdue =
          (formattedValue ?? row.dueAt) &&
          ((!row.completedDate && moment(formattedValue ?? row.dueAt) < moment()) ||
            (row.completedDate && moment(formattedValue ?? row.dueAt) < moment()));
        return (
          (formattedValue ?? row.dueAt) && (
            <Typography style={{ color: overdue ? red[500] : '', fontWeight: overdue ? 500 : 400 }} noWrap>
              {(formattedValue ?? row.dueAt) && (
                <DateFormatter date={formattedValue ?? row.dueAt} formatFunction={FormatDate} />
              )}
            </Typography>
          )
        );
      },
    };
    const completedColumns = viewCompleted
      ? [
          dueAtColumn,
          {
            field: 'completedDate',
            headerName: 'Completed',
            sortable: true,
            width: 150,
            alignItems: 'center',
            display: 'flex',
            filterOperators: Object.keys(completedDateFilters).map((filter) => ({
              label: convertToTitleCase(filter),
              value: filter,
            })),
            renderCell: ({ row, rowNode }) =>
              rowNode.type !== 'group' && (
                <Typography noWrap>
                  {row.completedDate ? <DateFormatter date={row.completedDate} formatFunction={FormatDate} /> : ''}
                </Typography>
              ),
          },
          {
            field: 'deltaMs',
            headerName: 'Task delta',
            sortable: true,
            width: 200,
            align: 'center',
            filterable: false,
            display: 'flex',
            renderCell: ({ row, rowNode }) =>
              rowNode.type !== 'group' && (row.dueAt ? formatDeltaForTable(row.deltaMs) : ''),
          },
        ]
      : [
          dueAtColumn,
          {
            field: 'statusId',
            headerName: 'Status',
            sortable: true,
            width: 200,
            alignItems: 'center',
            display: 'flex',
            filterOperators: [
              {
                label: 'Is any of',
                value: 'isAnyOf',
                InputComponent: StatusInput,
                InputComponentProps: { statusGroups: taskInfo?.statusGroups || [] },
              },
            ],
            renderCell: ({ row, rowNode, formattedValue, field }) => {
              if (rowNode.type === 'group' && field === rowNode.groupingField) {
                return '';
              }
              return (
                (formattedValue ?? row.progress) && (
                  <StatusButton
                    status={row.progress ?? formattedValue}
                    setStatus={(status) => changeStatus(row._id, status)}
                    isDisabled={disabledTypes.includes(row.type)}
                    isOverdue={row.isOverdue}
                    projectStatuses={row?.projectStatuses}
                  />
                )
              );
            },
            groupingValueGetter: (value, row) => {
              return row.progress;
            },
          },
        ];

    const ownerColumn = {
      field: 'created_by',
      headerName: 'Task owner',
      sortable: false,
      width: 300,
      align: 'left',
      display: 'flex',
      filterOperators: [
        {
          label: 'Includes',
          value: 'includes',
          InputComponent: UserInputValue,
          InputComponentProps: { type: 'user' },
          getValueAsString: (value) => (isArray(value) ? value.join(', ') : ''),
        },
      ],
      renderCell: (params) => {
        if (params.rowNode.type === 'group' && params.field === params.rowNode.groupingField) {
          return '';
        }
        return (
          (params.formattedValue ?? params.row.created_by) && (
            <User
              id={params.formattedValue ?? params.row.created_by}
              avatarSize={28}
              withoutPadding
              customUserPadding={10}
            />
          )
        );
      },
    };

    const assignedColumn = {
      field: 'assignedUsers',
      headerName: 'Assigned',
      sortable: false,
      width: 300,
      align: 'left',
      display: 'flex',
      filterOperators: [
        {
          label: 'Includes',
          value: 'includes',
          InputComponent: UserInputValue,
          InputComponentProps: { type: 'user' },
          getValueAsString: (value) => (isArray(value) ? value.join(', ') : ''),
        },
      ],
      renderCell: ({ row, rowNode, formattedValue, field }) => {
        if (rowNode.type === 'group' && field === rowNode.groupingField) {
          return '';
        }
        return (
          (formattedValue ?? row.assignedUsers) && (
            <UserGroup ids={formattedValue ?? row.assignedUsers} avatarSize={32} />
          )
        );
      },
    };

    const userColumns =
      pageType === 'my' ? [ownerColumn] : pageType === 'assigned' ? [assignedColumn] : [ownerColumn, assignedColumn];

    const referenceColumns =
      includeRefColumns && !forHomePage
        ? [
            {
              field: 'form',
              headerName: 'Form',
              width: 180,
              align: 'left',
              display: 'flex',
              valueGetter: (value, row) => row._id || null,
              sortable: false,
              filterOperators: [
                {
                  label: 'Is any of',
                  value: 'isAnyOf',
                  InputComponent: OptionsInput,
                  InputComponentProps: {
                    options: taskInfo.forms,
                  },
                },
              ],
              renderCell: ({ row }) => (
                <TooltipTypography sx={{ fontWeight: 500 }}>{row.form?.title || '-'}</TooltipTypography>
              ),
            },
            {
              field: 'process',
              headerName: 'Process',
              width: 180,
              align: 'left',
              sortable: false,
              display: 'flex',
              valueGetter: (value, row) => row._id || null,
              filterOperators: [
                {
                  label: 'Is any of',
                  value: 'isAnyOf',
                  InputComponent: OptionsInput,
                  InputComponentProps: {
                    options: taskInfo.processes,
                  },
                },
              ],
              renderCell: ({ row }) => (
                <TooltipTypography sx={{ fontWeight: 500 }}>{row.process?.title || '-'}</TooltipTypography>
              ),
            },
          ]
        : [];

    const allColumns = [
      ...(!forHomePage
        ? [
            {
              field: 'priority',
              headerName: 'Priority',
              sortable: true,
              width: 60,
              align: 'center',
              display: 'flex',
              filterOperators: [
                {
                  label: 'Is any of',
                  value: 'isAnyOf',
                  InputComponent: OptionsInput,
                  InputComponentProps: {
                    options: PrioritiesOptions,
                  },
                },
              ],
              renderCell: ({ row, rowNode, formattedValue, field }) => {
                if (rowNode.type === 'group' && field === rowNode.groupingField) {
                  return '';
                }
                return (
                  (formattedValue ?? row.priority) && (
                    <Grid
                      container
                      item
                      justifyContent="center"
                      alignItems="center"
                      style={{ height: '100%' }} // Ensure the Grid item takes full height of the cell
                    >
                      <TaskPriorityButton
                        priority={formattedValue ?? row.priority}
                        setPriority={(priority) => changePriority(row._id, priority)}
                      />
                    </Grid>
                  )
                );
              },
            },
          ]
        : []),
      {
        field: 'title',
        headerName: 'Task name',
        width: !forHomePage ? 180 : 360,
        align: 'left',
        groupable: false,
        display: 'flex',
        renderCell: ({ row, rowNode, formattedValue, field }) => {
          if (rowNode.type === 'group' && field === rowNode.groupingField) {
            return '';
          }
          return (
            (formattedValue ?? row.title) && (
              <Grid container item wrap="nowrap" alignItems="center" height={'100%'}>
                {row.flagged && <FlagRounded sx={{ color: red[500], mr: 1 }} />}
                <TooltipTypography sx={{ fontWeight: 500 }}>{formattedValue ?? row.title}</TooltipTypography>
              </Grid>
            )
          );
        },
      },
      ...userColumns,
      ...(!forHomePage
        ? [
            {
              field: 'sourceTitle',
              headerName: 'Source',
              width: 180,
              alignItems: 'left',
              display: 'flex',
              justifyContent: 'center',
              renderCell: ({ row, rowNode }) => {
                if (rowNode.type === 'group' && rowNode.groupingField === 'sourceTitle') {
                  return (
                    <Grid container item alignItems="center" wrap="nowrap">
                      <Typography>{rowNode.groupingKey}</Typography>
                    </Grid>
                  );
                }
                const taskTypeInfo = TaskTypes[row.type] ?? TaskTypes.workflow;
                return (
                  <Grid container item alignItems="center" wrap="nowrap">
                    {taskTypeInfo.icon}
                    <Typography sx={{ ml: 1 }}>{row.sourceTitle || taskTypeInfo.text}</Typography>
                  </Grid>
                );
              },
            },
            {
              // this column is always hidden
              headerName: 'Type',
              field: 'type',
              filterOperators: [
                {
                  label: 'Is any of',
                  value: 'isAnyOf',
                  InputComponent: OptionsInput,
                  InputComponentProps: {
                    options: isArray(taskInfo?.types)
                      ? taskInfo.types?.map((type) => ({
                          _id: type,
                          title: TaskTypes[type]?.text || '',
                        }))
                      : [],
                  },
                },
              ],
            },
          ]
        : []),
      ...completedColumns,
      ...referenceColumns,
    ];

    return allColumns;
  }, [viewCompleted, taskInfo, pageType, includeRefColumns]); // eslint-disable-line

  const LoadTasksUrls = {
    my: '/tasks/data/loadMyTasksTable',
    assigned: '/tasks/data/loadAssignedTasksTable',
    watching: '/tasks/data/loadWatchingTasksTable',
    team: '/tasks/data/loadTeamTaskTable',
    portal: '/tasks/data/loadPortalTasksTable',
  };

  const LoadCompletedTasksUrls = {
    my: '/tasks/data/loadMyTasksCompletedTable',
    assigned: '/tasks/data/loadAssignedTasksCompletedTable',
    watching: '/tasks/data/loadWatchingTasksCompletedTable',
    team: '/tasks/data/loadTeamTasksCompletedTable',
    portal: '/tasks/data/loadPortalTasksCompletedTable',
  };

  const dataGridClass = `${pageType}${teamId || ''}${portalId || ''}-tasks-${viewCompleted ? 'completed' : 'working'}`;

  const url = viewCompleted ? LoadCompletedTasksUrls[pageType] : LoadTasksUrls[pageType];

  const gridConfig = useDataGridPagination(dataGridClass, columns, url, 'tasks', 'totalCount', 'personal', {
    requestData: { teamId, portalId },
    checkboxPosition: 'left',
  });

  // tasks update/delete events
  useEffect(() => {
    const taskUpdateSubscribe = TaskManager.subscribeToAnyTaskUpdate(({ task }) => {
      if (!task) return;

      const tasksCopy = gridConfig.getRows();
      const index = tasksCopy.findIndex((row) => row._id === task._id);

      if (index === -1) return;

      tasksCopy[index] = { ...tasksCopy[index], ...task };

      // if task has been completed and we are not in completed view
      if (task.completedDate && !viewCompletedRef.current) {
        gridConfig.updateRows(tasksCopy.splice(index, 1));

        // reload to get the missing task
        gridConfig.reloadCurrentPage(true);
      } else {
        gridConfig.updateRows(tasksCopy);
      }
    });
    const taskCreateSubscribe = TaskManager.subscribeToNewTask(() => {
      gridConfig.reloadCurrentPage(true);
    });
    const taskDeleteSubscribe = TaskManager.subscribeToAnyTaskDelete(({ taskIds }) => {
      if (!isArray(taskIds)) return;
      // reload the page if there is at least 1 task in the current page in the list
      if (!gridConfig.getRows().some((x) => taskIds.includes(x._id))) return;

      gridConfig.reloadCurrentPage(true);
    });
    return () => {
      taskUpdateSubscribe.unsubscribe();
      taskCreateSubscribe.unsubscribe();
      taskDeleteSubscribe.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function changePriority(taskId, priority) {
    const update = {
      field: 'priority',
      value: priority,
    };
    axios
      .post('/tasks/saveTask', { taskId, update })
      .catch((err) => StateManager.setErrorAlert(err.response.data.message));
  }

  function changeStatus(taskId, progress) {
    const update = {
      field: 'progress',
      value: progress,
    };
    axios
      .post('/tasks/saveTask', { taskId, update })
      .then(({ data }) => {
        if (data.completedDate && !viewCompletedRef.current) {
          // reload to get the missing task
          gridConfig.reloadCurrentPage(true);
        } else {
          // update the task in the table
          const tasksCopy = gridConfig.getRows();
          const index = tasksCopy.findIndex((row) => row._id === taskId);
          if (index === -1) return;

          tasksCopy[index] = { ...tasksCopy[index], progress };
        }
      })
      .catch((err) => StateManager.setErrorAlert(err.response.data.message));
  }

  const onUpdate = (task) => {
    if (!task) return;
    const tasksCopy = gridConfig.getRows();
    const index = tasksCopy.findIndex((row) => row._id === task?._id);
    if (index === -1) return;

    tasksCopy[index] = { ...tasksCopy[index], ...task };

    if (task.completedDate && !viewCompleted) {
      gridConfig.updateRows(tasksCopy.splice(index, 1));

      // reload to get the missing task
      gridConfig.reloadCurrentPage(true);
    } else {
      gridConfig.updateRows(tasksCopy);
    }
  };

  const onDelete = () => {
    gridConfig.reloadCurrentPage();
  };

  function openTask(task) {
    if (task.type === 'audit' && fromPortal) {
      history.push(`/portal/audits/${task.auditId}`);
      return;
    }
    if (task.type === 'process') {
      history.push(`/processes/ongoing/${task.processId}`);
      return;
    }
    setHighlightedTaskId(task._id);
    StateManager.selectTask(task._id, onUpdate, onDelete);
  }

  function confirmDelete(e) {
    const choosenTasksList = gridConfig.gridProps.rows.filter((task) => selected.includes(task._id));
    const affectedForms = choosenTasksList.filter((task) => task.type === 'form_entry');
    const affectedProcesses = choosenTasksList.filter((task) => task.type === 'process_step');

    const affectedFormsText = affectedForms.length ? `${affectedForms.length} Form(s)\n` : '';
    const affectedProcessesText = affectedProcesses.length ? `${affectedProcesses.length} Process(es)\n` : '';

    StateManager.setConfirm(
      `You are about to delete the selected tasks.`,
      deleteSelected,
      ` ${
        affectedForms.length || affectedProcesses.length ? 'It will affect following:' : 'Are you sure?'
      } ${affectedFormsText} ${affectedProcessesText}`,
    );
  }

  function deleteSelected() {
    setDeleting(true);
    axios
      .post('/tasks/management/deleteTasks', { taskIds: selected })
      .then(({ data }) => onDelete(data.deletedIds))
      .catch((err) => StateManager.setAxiosErrorAlert(err))
      .finally(() => setDeleting(false));
    setSelected([]);
  }

  return (
    <Grid container sx={{ flex: 1, height: dateView && 500 }}>
      <Grid
        direction={'column'}
        container
        sx={{
          display: 'flex',
          height: '100%',
          maxHeight: '100%',
          flexWrap: 'nowrap',
        }}
      >
        <Grid container sx={{ mb: 1 }}>
          {dateView ? (
            <>
              {!isEmpty(selected) && (
                <Grid item>
                  <Button
                    sx={{ color: red[500], ml: 'auto' }}
                    startIcon={deleting ? <CircularProgress size={24} /> : <DeleteOutlineRounded />}
                    onClick={(e) => confirmDelete(e)}
                  >
                    delete selected
                  </Button>
                </Grid>
              )}
            </>
          ) : (
            <>
              <TasksFilters
                selectedFilters={gridConfig.gridProps.filterModel}
                setSelectedFilters={(model) => {
                  // to avoid double load
                  if (isEqual(model, gridConfig.gridProps.filterModel)) return;

                  onFiltersUpdate(model);
                }}
                taskInfo={taskInfo}
                pageType={pageType}
                searchTerm={searchTerm}
                includeRefColumns={includeRefColumns}
                startItems={
                  !dateView && (
                    <Grid item>
                      <Button
                        startIcon={viewCompleted ? <CachedRounded /> : <CheckCircleOutline />}
                        onClick={() => setViewCompleted((prev) => !prev)}
                        variant="outlined"
                      >
                        {viewCompleted ? 'Show In Progress' : 'Show Completed'}
                      </Button>
                    </Grid>
                  )
                }
                endItems={
                  !isEmpty(selected) && (
                    <Grid item>
                      <Button
                        sx={{ color: red[500], ml: 'auto' }}
                        startIcon={deleting ? <CircularProgress size={24} /> : <DeleteOutlineRounded />}
                        onClick={(e) => confirmDelete(e)}
                      >
                        delete selected
                      </Button>
                    </Grid>
                  )
                }
                gridConfig={gridConfig?.gridProps}
                defaultView={gridConfig?.defaultView}
                configId={gridConfig?.configId}
                onConfigChange={gridConfig?.onConfigurationChange}
                views={gridConfig.views}
                forHomePage={forHomePage}
              />
            </>
          )}
        </Grid>

        <Grid container sx={{ flex: 1, position: 'relative' }}>
          <Grid
            container
            sx={{
              height: '100%',
              position: 'absolute',
              background: (theme) => theme.palette.background.paper,
            }}
          >
            <DataGridPremium
              disableAggregation
              {...gridConfig.gridProps}
              columnVisibilityModel={{ ...gridConfig.gridProps.columnVisibilityModel, type: false }}
              onFilterModelChange={onFiltersUpdate}
              onRowClick={({ row }) => {
                openTask(row);
              }}
              disableRowSelectionOnClick
              checkboxSelection
              rowSelectionModel={selected}
              onRowSelectionModelChange={setSelected}
              sx={{
                '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
                height: '100%',
                maxHeight: '100%',
                ...(gridConfig?.gridProps?.rowGroupingModel?.length === 0 && {
                  '& .highlighted-row': {
                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? blue[600] : blue[200]),
                    '&:hover': {
                      backgroundColor: (theme) => (theme.palette.mode === 'dark' ? blue[700] : blue[300]),
                    },
                  },
                }),
                '& .pending-row': {
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? amber[800] : amber[200]),
                  '&:hover': {
                    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? amber[900] : amber[300]),
                  },
                },
              }}
              getRowClassName={({ row }) => {
                if (row.acceptance === 'pending') {
                  return 'pending-row';
                }
                if (row._id === highlightedTaskId) {
                  return 'highlighted-row';
                }
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
